export enum SkinType {
  OILY = 'OILY',
  DRY = 'DRY',
  COMBINATION = 'COMBINATION',
  NORMAL = 'NORMAL',
}

export enum AgeRange {
  UNDER_18 = 'UNDER_18',
  BETWEEN_18_24 = 'BETWEEN_18_24',
  BETWEEN_25_34 = 'BETWEEN_25_34',
  BETWEEN_35_44 = 'BETWEEN_35_44',
  OVER_45 = 'OVER_45',
}

export enum SensitivityLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum SunExposure {
  LOW = 'LOW',
  MODERATE = 'MODERATE',
  HIGH = 'HIGH',
}

export enum SunscreenUsage {
  NEVER = 'NEVER',
  SOMETIMES = 'SOMETIMES',
  DAILY = 'DAILY',
}

export enum WaterIntake {
  LOW = 'LOW',
  MODERATE = 'MODERATE',
  HIGH = 'HIGH',
}

export enum SleepQuality {
  POOR = 'POOR',
  AVERAGE = 'AVERAGE',
  GOOD = 'GOOD',
}

export enum SkincareIngredient {
  FRAGRANCE = 'FRAGRANCE',
  ALCOHOL = 'ALCOHOL',
  PARABEN = 'PARABEN',
  SULFATE = 'SULFATE',
  SILICONE = 'SILICONE',
  ESSENTIAL_OILS = 'ESSENTIAL_OILS',
  RETINOL = 'RETINOL',
  AHA = 'AHA',
  BHA = 'BHA',
  NIACINAMIDE = 'NIACINAMIDE',
  SALICYLIC_ACID = 'SALICYLIC_ACID',
  BENZOYL_PEROXIDE = 'BENZOYL_PEROXIDE',
  VITAMIN_C = 'VITAMIN_C',
  HYALURONIC_ACID = 'HYALURONIC_ACID',
}

export enum Period {
  MORNING = 'MORNING',
  NIGHT = 'NIGHT',
}

/** Tipo de pele para o qual um produto do catálogo é indicado. */
export enum SkinTag {
  ALL = 'ALL',
  OILY = 'OILY',
  DRY = 'DRY',
  COMBINATION = 'COMBINATION',
  NORMAL = 'NORMAL',
  SENSITIVE = 'SENSITIVE',
  ACNE_PRONE = 'ACNE_PRONE',
}

/** Rótulo de cada indicação, usado na gaveta, no modal e no formulário do painel. */
export const PELES: Record<SkinTag, string> = {
  [SkinTag.ALL]: 'Todos os tipos',
  [SkinTag.OILY]: 'Pele oleosa',
  [SkinTag.DRY]: 'Pele seca',
  [SkinTag.COMBINATION]: 'Pele mista',
  [SkinTag.NORMAL]: 'Pele normal',
  [SkinTag.SENSITIVE]: 'Pele sensível',
  [SkinTag.ACNE_PRONE]: 'Pele acneica',
};

/**
 * Ritmo de rotina que o usuário aceita seguir. É ele que limita quantos passos a IA
 * recomenda por turno — os números batem com o enum RoutinePace do backend.
 */
export enum RoutinePace {
  PRACTICAL = 'PRACTICAL',
  BALANCED = 'BALANCED',
  UNHURRIED = 'UNHURRIED',
}

// ── Anamnese de feedback (ciclo de 30 dias) ────────────────────────────────────

/** Como a pele está desde a última anamnese. */
export enum SkinChange {
  MUCH_BETTER = 'MUCH_BETTER',
  BETTER = 'BETTER',
  SAME = 'SAME',
  WORSE = 'WORSE',
}

/** Quanto da rotina o usuário conseguiu cumprir no ciclo. */
export enum RoutineAdherence {
  ALWAYS = 'ALWAYS',
  MOSTLY = 'MOSTLY',
  SOMETIMES = 'SOMETIMES',
  RARELY = 'RARELY',
}

/** Se o tempo da rotina coube no dia do usuário. */
export enum RoutineTimeFit {
  TOO_LONG = 'TOO_LONG',
  JUST_RIGHT = 'JUST_RIGHT',
  TOO_SHORT = 'TOO_SHORT',
}

export enum StepReaction {
  GREAT = 'GREAT',
  NEUTRAL = 'NEUTRAL',
  IRRITATED = 'IRRITATED',
}

export enum LogType {
  ROUTINE = 'ROUTINE',
  SKIN = 'SKIN',
}

export enum ExpiryStatus {
  NO_DATE = 'NO_DATE',
  OK = 'OK',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED',
}

export enum UserProfile {
  /** Perfil padrão do cadastro. Precisa de assinatura ativa para usar o app. */
  USUARIO = 'USUARIO',
  /** Parceiro comercial: acesso de usuário, isento de pagamento. Só via banco. */
  PARCEIRO = 'PARCEIRO',
  /** Super admin: acesso irrestrito e isento de pagamento. Só via banco. */
  DOMINUS = 'DOMINUS',
}

/**
 * Identificador dos planos. Só a identidade mora aqui: nome, valor e periodicidade
 * são editáveis pelo DOMINUS e chegam do backend a cada consulta.
 */
export enum SubscriptionPlan {
  MENSAL = 'MENSAL',
  TRIMESTRAL = 'TRIMESTRAL',
  SEMESTRAL = 'SEMESTRAL',
}

/** Periodicidade da cobrança de um plano — os nomes são os ciclos aceitos pelo Asaas. */
export enum BillingCycle {
  MONTHLY = 'MONTHLY',
  BIMONTHLY = 'BIMONTHLY',
  QUARTERLY = 'QUARTERLY',
  SEMIANNUALLY = 'SEMIANNUALLY',
  YEARLY = 'YEARLY',
}

/** Rótulo em português de cada periodicidade, para o select do painel. */
export const CICLOS: Record<BillingCycle, string> = {
  [BillingCycle.MONTHLY]: 'Mensal',
  [BillingCycle.BIMONTHLY]: 'Bimestral',
  [BillingCycle.QUARTERLY]: 'Trimestral',
  [BillingCycle.SEMIANNUALLY]: 'Semestral',
  [BillingCycle.YEARLY]: 'Anual',
};

/**
 * Forma de pagamento do checkout transparente — os nomes são os do Asaas.
 * O `UNDEFINED` dele não existe aqui: quem pergunta como pagar é esta tela.
 */
export enum BillingType {
  CREDIT_CARD = 'CREDIT_CARD',
  PIX = 'PIX',
  BOLETO = 'BOLETO',
}

/** Rótulo de cada forma de pagamento, para as abas do checkout. */
export const FORMAS_PAGAMENTO: Record<BillingType, string> = {
  [BillingType.CREDIT_CARD]: 'Cartão',
  [BillingType.PIX]: 'Pix',
  [BillingType.BOLETO]: 'Boleto',
};

/** Situação da assinatura. Quem manda no valor é o webhook do Asaas, não o app. */
export enum SubscriptionStatus {
  /** Cobrança gerada, pagamento ainda não confirmado. Sem acesso. */
  PENDING = 'PENDING',
  /** Pago e dentro do período. Com acesso. */
  ACTIVE = 'ACTIVE',
  /** Cobrança venceu sem pagamento. Sem acesso. */
  OVERDUE = 'OVERDUE',
  /** Cancelada, estornada ou removida. Sem acesso. */
  CANCELED = 'CANCELED',
}

export enum BugStatus {
  OPEN = 'OPEN',
  SEEN = 'SEEN',
  NOT_A_BUG = 'NOT_A_BUG',
  IN_PROGRESS = 'IN_PROGRESS',
  FIXED = 'FIXED',
}
